import React from "react";
import { Box, Typography, TextField, Paper } from "@material-ui/core";

const FieldRenderer = ({ label, value, isEditing, onChange}) => {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <Box mb={2}>
      <Typography variant="subtitle1" gutterBottom>
        {label.replace(/_/g, " ")}:
      </Typography>
      {isEditing ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={value || ""}
          onChange={handleChange} 
          onBlur={(e) => {
            const { value } = e.target;
            // Trim leading and trailing spaces for string values
            if (typeof value === "string" && value.trim() !== value) {
                handleChange({ target: { value: value.trim() } });
            }
          }}
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          {value !== null && value !== undefined ? value.toString() : "N/A"}
        </Typography>
      )}
    </Box>
  );
};

const DynamicFields = ({ data, isEditing, onChange }) => {
  // If it's not an object (i.e., primitive), render a single field
  if (typeof data !== "object" || data === null) {
    return (
      <FieldRenderer
        label="Value"
        value={data}
        isEditing={isEditing}
        onChange={(val) => onChange(val)}
      />
    );
  }

  // If it's an array, handle in ArrayRenderer
  if (Array.isArray(data)) {
    return (
      <ArrayRenderer
        label="Array"
        data={data}
        isEditing={isEditing}
        onChange={onChange}
      />
    );
  }

  // Otherwise, it's an object—iterate its keys
  return (
    <>
      {Object.entries(data).map(([key, value]) => {
        // If the value is an array
        if (Array.isArray(value)) {
          return (
            <ArrayRenderer
              key={key}
              label={key}
              data={value}
              isEditing={isEditing}
              onChange={(updatedArray) =>
                onChange({ ...data, [key]: updatedArray })
              }
            />
          );
        } else if (typeof value === "object" && value !== null) {
          // Nested object
          return (
            <Box mb={2} key={key}>
              <Typography variant="subtitle1" gutterBottom>
                {key.replace(/_/g, " ")}:
              </Typography>
              <Box ml={2}>
                <DynamicFields
                  data={value}
                  isEditing={isEditing}
                  onChange={(updatedValue) =>
                    onChange({ ...data, [key]: updatedValue })
                  }
                />
              </Box>
            </Box>
          );
        } else {
          // Primitive field
          return (
            <FieldRenderer
              key={key}
              label={key}
              value={value}
              isEditing={isEditing}
              onChange={(val) => onChange({ ...data, [key]: val })}
            />
          );
        }
      })}
    </>
  );
};

/**
 * ArrayRenderer logic that:
 * - If there's only one item and it's a primitive => inline
 * - If multiple items (primitives) => multiple lines
 * - If any items are objects => render sub-blocks
 */
const ArrayRenderer = ({ label, data, isEditing, onChange }) => {
  // Utility: check if every item in the array is a primitive
  const isPrimitive = (item) => item === null || typeof item !== "object";
  const allPrimitives = data.every(isPrimitive);

  // If array has only one item and that item is a primitive => inline field
  if (data.length === 1 && allPrimitives) {
    return (
      <FieldRenderer
        label={label}
        value={data[0]}
        isEditing={isEditing}
        onChange={(val) => {
          const newArray = [...data];
          newArray[0] = val;
          onChange(newArray);
        }}
      />
    );
  }

  // If array is all primitives but more than one => multiple lines
  if (allPrimitives) {
    return (
      <Box mb={2}>
        <Typography variant="subtitle1" gutterBottom>
          {label.replace(/_/g, " ")}:
        </Typography>
        {data.map((item, index) => (
          <Box key={index} ml={2} mt={1}>
            {isEditing ? (
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={item || ""}
                onChange={(e) => {
                  const newArray = [...data];
                  newArray[index] = e.target.value;
                  onChange(newArray);
                }}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                {item !== null && item !== undefined ? item.toString() : "N/A"}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Otherwise, array has objects or nested structures => render each as a sub-block
  return (
    <Box mb={2}>
      <Typography variant="subtitle1" gutterBottom>
        {label.replace(/_/g, " ")}:
      </Typography>
      {data.map((item, index) => (
        <Box key={index} ml={2} mt={1} mb={2}>
          <Paper style={{ padding: 16 }}>
            <DynamicFields
              data={item}
              isEditing={isEditing}
              onChange={(updatedItem) => {
                const newArray = [...data];
                newArray[index] = updatedItem;
                onChange(newArray);
              }}
            />
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

export const JSONEditor = ({ data, isEditing, onChange,documentCategory }) => {
  const handleBlockChange = (blockName, updatedValue) => {
    onChange({ ...data, [blockName]: updatedValue });
  };

  // If data is not an object, just show a fallback
  if (typeof data !== "object" || data === null) {
    return <div>No valid data</div>;
  }
//console.log("datajsonEDitor",data)
//console.log("documentCategory",documentCategory)
//console.log("data.coc",data.cocData)
const updatedObject = {
  ...data,
  [`${documentCategory} Data`]: data.cocData
};

delete updatedObject.cocData;

//console.log(updatedObject);
  return (
    <Box p={3} maxHeight={800} overflow="auto" style={{ backgroundColor: "#fff" }}>
      {Object.entries(updatedObject).map(([blockName, blockValue]) =>(
    
     <Box key={blockName} mb={4}>
          <Typography variant="h6" gutterBottom>
            {blockName.replace(/_/g, " ")}
          </Typography>
          <Paper elevation={3} style={{ padding: 16 }}>
            <DynamicFields
              data={blockValue}
              isEditing={isEditing}
              onChange={(updatedValue) => handleBlockChange(blockName, updatedValue)}
            />
          </Paper>
        </Box>
      ))}
    </Box>
     );
};
